import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faDollarSign, 
  faArrowRight, 
  faCreditCard, 
  faHeart, 
  faCheck, 
  faPhone, 
  faEnvelope, 
  faLocationDot, 
  faTimes,
  faChevronRight,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { Modal, Form, InputGroup } from 'react-bootstrap';
import { useLanguage } from '../config/LanguageContext';
import SuccessLottie from '../component/successLottie';
import { getCreditCards } from '../actions/common';

const CardFinder = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [answers, setAnswers] = useState({
    salary: '',
    cardType: [],
    features: []
  });
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [noEligibleCards, setNoEligibleCards] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [showCardModal, setShowCardModal] = useState(false);
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [applicationData, setApplicationData] = useState({
    fullName: '',
    email: '',
    mobile: ''
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [allCards, setAllCards] = useState([]);
  const { t, currentLanguage } = useLanguage();

  // Fetch cards from Strapi on component mount
  useEffect(() => {
    const fetchCards = async () => {
      try {
        const { status, result, error } = await getCreditCards();
        if (status === "success" && result) {
          setAllCards(result);
        } else {
          console.error("Failed to fetch cards:", error);
        }
      } catch (err) {
        console.error("Error fetching cards:", err);
      }
    };
    fetchCards();
  }, []);

  const questions = [
    {
      step: 1,
      icon: faDollarSign,
      title: t('monthlySalary'),
      subtitle: t('salaryHelp'),
      options: [
        { value: "3000-5000", label: t('salary3k5k') },
        { value: "5000-10000", label: t('salary5k10k') },
        { value: "10000-20000", label: t('salary10k20k') },
        { value: "20000+", label: t('salary20kPlus') }
      ]
    },
    {
      step: 2,
      icon: faCreditCard,
      title: t('cardType'),
      subtitle: t('cardTypeHelp'),
      options: [
        { value: "cashback", label: t('cashbackRewardsCard') },
        { value: "travel", label: t('travelPremiumCard') },
        { value: "lifestyle", label: t('lifestyleShoppingCard') },
        { value: "luxury", label: t('luxuryExclusiveCard') }
      ]
    },
    {
      step: 3,
      icon: faHeart,
      title: t('importantFeatures'),
      subtitle: t('featuresHelp'),
      options: [
        { value: "low-fees", label: t('lowAnnualFees') },
        { value: "high-rewards", label: t('highRewardRates') },
        { value: "luxury-perks", label: t('luxuryPerks') },
        { value: "simple-cashback", label: t('simpleCashback') }
      ]
    }
  ];

  const currentQuestion = questions.find(q => q.step === currentStep);
  const progressPercentage = (currentStep / 3) * 100;

  // Handler for single selection (salary)
  const handleSingleAnswerSelect = (value, label) => {
    setAnswers(prev => ({ ...prev, salary: value }));
    
    setTimeout(() => {
      setCurrentStep(currentStep + 1);
    }, 300);
  };

  // Handler for multiple selection (card types and features)
  const handleMultipleAnswerSelect = (value, label) => {
    const answerKey = currentStep === 2 ? 'cardType' : 'features';
    
    setAnswers(prev => {
      const currentSelections = prev[answerKey];
      const isAlreadySelected = currentSelections.includes(value);
      
      if (isAlreadySelected) {
        // Remove the selection
        return {
          ...prev,
          [answerKey]: currentSelections.filter(item => item !== value)
        };
      } else {
        // Add the selection
        return {
          ...prev,
          [answerKey]: [...currentSelections, value]
        };
      }
    });
  };

  // Handler for proceeding to next step (for multiple choice questions)
  const handleNextStep = () => {
    const answerKey = currentStep === 2 ? 'cardType' : 'features';
    const currentSelections = answers[answerKey];
    
    if (currentSelections.length === 0) {
      alert('Please select at least one option before proceeding.');
      return;
    }

    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    } else {
      // Final step - show loading and then results
      setIsLoading(true);
      setTimeout(() => {
        setIsLoading(false);
        // Filter cards based on user's salary vs card minimum salary requirements
        let filteredCards = [];
        
        // Filter by salary - user must earn AT LEAST the card's minimum salary
        const salaryValue = answers.salary;
        if (salaryValue === "3000-5000") {
          filteredCards = allCards.filter(card => 
            (card.minimumSalary || 0) <= 5000
          );
        } else if (salaryValue === "5000-10000") {
          filteredCards = allCards.filter(card => 
            (card.minimumSalary || 0) <= 10000
          );
        } else if (salaryValue === "10000-20000") {
          filteredCards = allCards.filter(card => 
            (card.minimumSalary || 0) <= 20000
          );
        } else if (salaryValue === "20000+") {
          filteredCards = allCards.filter(card => 
            (card.minimumSalary || 0) <= 50000
          );
        }
        
        // Further filter by card type preferences (multiple selections allowed)
        const selectedCardTypes = answers.cardType;
        if (selectedCardTypes.length > 0 && filteredCards.length > 0) {
          const typeFilteredCards = filteredCards.filter(card => {
            const cardName = (card.name || '').toLowerCase();
            const cardDesc = (card.shortDescription || '').toLowerCase();
            
            return selectedCardTypes.some(selectedType => {
              if (selectedType === "luxury") return cardName.includes('infinite') || cardName.includes('solitaire') || cardName.includes('black') || cardName.includes('elevate');
              if (selectedType === "cashback") return cardName.includes('cashback') || cardDesc.includes('cashback');
              if (selectedType === "lifestyle") return cardName.includes('lifestyle') || cardName.includes('shopping') || cardName.includes('noon') || cardName.includes('emaar') || cardName.includes('darna');
              if (selectedType === "travel") return cardName.includes('travel') || cardName.includes('miles') || cardName.includes('skywards') || cardName.includes('etihad') || cardName.includes('dnata');
              return false;
            });
          });
          
          // Only apply type filter if it returns results
          if (typeFilteredCards.length > 0) {
            filteredCards = typeFilteredCards;
          }
        }
        
        // Check if user has eligible cards
        if (filteredCards.length === 0) {
          setNoEligibleCards(true);
          setShowResults(true);
        } else {
          setNoEligibleCards(false);
          setResults(filteredCards);
          setShowResults(true);
        }
      }, 2500);
    }
  };

  const handleCardDetails = (card) => {
    setSelectedCard(card);
    setShowCardModal(true);
  };

  const handleApplyNow = (card) => {
    setSelectedCard(card);
    setShowCardModal(false);
    setShowApplicationForm(true);
  };

  const handleApplicationSubmit = (e) => {
    e.preventDefault();
    // Here you would typically send the data to your backend
    console.log('Application submitted:', { ...applicationData, cardId: selectedCard.id });
    
    setShowApplicationForm(false);
    setShowSuccess(true);
    
    // Reset after 3 seconds
    setTimeout(() => {
      setShowSuccess(false);
      resetFinder();
    }, 3000);
  };

  const resetFinder = () => {
    setCurrentStep(1);
    setAnswers({ salary: '', cardType: [], features: [] });
    setShowResults(false);
    setResults([]);
    setNoEligibleCards(false);
    setApplicationData({ fullName: '', email: '', mobile: '' });
  };

  const renderCreditCardSVG = (card) => (
    <svg width="100" height="63" viewBox="0 0 100 63" className="credit-card-svg">
      <defs>
        <linearGradient id={`cardGradient-${card.bank}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={card.color} />
          <stop offset="100%" stopColor={`${card.color}CC`} />
        </linearGradient>
        <filter id="cardShadow">
          <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.3" />
        </filter>
      </defs>
      <rect width="100" height="63" rx="6" fill={`url(#cardGradient-${card.bank})`} filter="url(#cardShadow)" />
      <rect x="8" y="15" width="12" height="10" rx="2" fill="rgba(255,255,255,0.9)" />
      <rect x="9" y="16" width="10" height="8" rx="1" fill="rgba(255,255,255,0.7)" />
      <g fill="rgba(255,255,255,0.8)">
        <circle cx="8" cy="38" r="1" />
        <circle cx="12" cy="38" r="1" />
        <circle cx="16" cy="38" r="1" />
        <circle cx="20" cy="38" r="1" />
        <circle cx="28" cy="38" r="1" />
        <circle cx="32" cy="38" r="1" />
        <circle cx="36" cy="38" r="1" />
        <circle cx="40" cy="38" r="1" />
      </g>
      <text x="50" y="42" fill="white" fontSize="6" fontFamily="monospace">1234</text>
      <text x="8" y="55" fill="rgba(255,255,255,0.9)" fontSize="5" fontWeight="bold">{card.bank}</text>
      <circle cx="85" cy="15" r="6" fill="rgba(255,255,255,0.2)" />
      <circle cx="85" cy="15" r="4" fill="rgba(255,255,255,0.4)" />
    </svg>
  );

  if (showSuccess) {
    return (
      <div className="card-finder-page">
        <section className="quick-card-finder py-5 pt-5 w-100">
          <div className="container position-relative">
            <div className="row justify-content-center">
              <div className="col-lg-6">
                <div className="quick-finder-card bg-white rounded-4 p-5 shadow-lg position-relative text-center">
                  <SuccessLottie />
                  <h3 className="h4 fw-bold mb-2" style={{ color: '#0f2b52' }}>
                    {t('applicationSubmitted')}
                  </h3>
                  <p style={{ color: '#0f2b52', opacity: 0.7 }}>
                    {t('thankYou')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="card-finder-page">
        <section className="quick-card-finder py-5 pt-5 w-100">
          <div className="container position-relative">
            <div className="row justify-content-center">
              <div className="col-lg-6">
                <div className="quick-finder-card bg-white rounded-4 p-5 shadow-lg position-relative text-center">
                  <div className="loading-animation mb-4">
                    <div className="rotating-card-container">
                      <div className="rotating-card">
                        <div className="card-face card-front">
                          <div className="card-chip"></div>
                          <div className="card-number">•••• •••• •••• ••••</div>
                          <div className="card-holder">CARD HOLDER</div>
                          <div className="card-brand">VISA</div>
                        </div>
                        <div className="card-face card-back">
                          <div className="magnetic-strip"></div>
                          <div className="cvv-area">
                            <div className="cvv-label">CVV</div>
                            <div className="cvv">•••</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="loading-dots mb-3">
                    <div className="dot"></div>
                    <div className="dot"></div>
                    <div className="dot"></div>
                  </div>
                  
                  <h3 className="h4 fw-bold mb-2" style={{ color: '#0f2b52' }}>
                    {t('findingCard')}
                  </h3>
                  <p style={{ color: '#0f2b52', opacity: 0.7 }}>
                    {t('analyzingPreferences')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (showResults) {
    return (
      <div className="card-finder-page">
        <section className="quick-card-finder py-5 pt-5 w-100">
          <div className="container position-relative">
            <div className="row justify-content-center mb-5">
              <div className="col-lg-8 text-center">
                <h2 className="display-5 fw-bold mb-3" style={{ color: '#0f2b52' }}>
                  {noEligibleCards ? t('noEligibleCards') : t('perfectMatches')}
                </h2>
                <p className="lead mb-4" style={{ color: '#0f2b52', opacity: 0.8 }}>
                  {noEligibleCards ? t('increaseIncomeMessage') : t('topRecommendations')}
                </p>
                <div 
                  className="btn-get-started scrollto pointer" 
                  onClick={resetFinder}
                >
                  {t('startOver')}
                </div>
              </div>
            </div>
            
            {noEligibleCards ? (
              <div className="row justify-content-center">
                <div className="col-lg-6 text-center">
                  <div className="alert alert-info p-4 rounded-4" style={{ backgroundColor: '#e3f2fd', border: 'none' }}>
                    <FontAwesomeIcon icon={faInfoCircle} className="mb-3" style={{ fontSize: '3rem', color: '#1976d2' }} />
                    <h5 className="fw-bold mb-3" style={{ color: '#0f2b52' }}>{t('sorryNoMatch')}</h5>
                    <p className="mb-4" style={{ color: '#0f2b52', opacity: 0.8 }}>
                      {t('minimumRequirement')}
                    </p>
                    <div className="mb-3">
                      <small className="text-muted">{t('ourLowestRequirement')}</small>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="row justify-content-center">
                {results.map((card) => {
                  const imageUrl = card.cardImage?.url 
                    ? `https://admin.fintrekmarketing.com${card.cardImage.url}`
                    : card.cardImage?.formats?.small?.url 
                      ? `https://admin.fintrekmarketing.com${card.cardImage.formats.small.url}`
                      : card.image;
                  
                  const benefits = card.featuresAndBenefits?.length 
                    ? card.featuresAndBenefits.map(b => b.title)
                    : card.benefits || [];

                  return (
                    <div key={card.id} className="col-lg-4 col-md-6 mb-4">
                      <div className="card h-100 shadow-lg border-0 rounded-4 overflow-hidden card-hover">
                        <div className="card-image-container" style={{ 
                          height: '180px', 
                          overflow: 'hidden',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: '#f8f9fa'
                        }}>
                          <img 
                            src={imageUrl} 
                            alt={card.name}
                            className="card-img-top"
                            style={{ 
                              width: '280px',
                              height: '175px',
                              objectFit: 'contain',
                              borderRadius: '8px'
                            }}
                          />
                        </div>
                        <div className="card-body p-4">
                          <h5 className="card-title fw-bold mb-2" style={{ color: '#0f2b52' }}>
                            {card.name}
                          </h5>
                          <div className="mb-3">
                            <p className="mb-1"><strong>{t('annualFee')}:</strong> AED {card.annualFee || 0}</p>
                            <p className="mb-1"><strong>{t('joiningFee')}:</strong> AED {card.joiningFee || 0}</p>
                            <p className="mb-1"><strong>{t('minimumSalary')}:</strong> AED {card.minimumSalary?.toLocaleString() || 'N/A'}</p>
                          </div>
                          
                          {benefits.length > 0 && (
                            <div className="mb-4">
                              <h6 className="fw-bold text-primary mb-2">{t('keyBenefits')}</h6>
                              {benefits.slice(0, 3).map((benefit, index) => (
                                <div key={index} className="d-flex align-items-center mb-1">
                                  <FontAwesomeIcon icon={faCheck} className="text-success me-2 flex-shrink-0" />
                                  <span className="small">{benefit}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="card-footer bg-transparent border-0 p-4 pt-0">
                          <div className="d-grid gap-2">
                            <button 
                              className="btn btn-primary btn-modern"
                              onClick={() => handleCardDetails(card)}
                            >
                              <FontAwesomeIcon icon={faChevronRight} className="me-2" />
                              {t('viewDetails')}
                            </button>
                            <button 
                              className="btn btn-outline-primary btn-modern"
                              onClick={() => handleApplyNow(card)}
                            >
                              <FontAwesomeIcon icon={faPhone} className="me-2" />
                              {t('applyNow')}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
            )}
          </div>
        </section>

        {/* Card Details Modal */}
        <Modal
          show={showCardModal}
          onHide={() => setShowCardModal(false)}
          size="lg"
          centered
        >
          {selectedCard && (
            <>
              <Modal.Header 
                closeButton 
                style={{ 
                  backgroundColor: '#0f2b52', 
                  border: 'none',
                  color: 'white'
                }}
              >
                <Modal.Title className="text-white fw-bold d-flex align-items-center">
                  <div className="me-3">
                    <img 
                      src={selectedCard.cardImage?.url 
                        ? `https://admin.fintrekmarketing.com${selectedCard.cardImage.url}`
                        : selectedCard.cardImage?.formats?.small?.url 
                          ? `https://admin.fintrekmarketing.com${selectedCard.cardImage.formats.small.url}`
                          : selectedCard.image} 
                      alt={selectedCard.name}
                      style={{ width: '60px', height: '38px', borderRadius: '6px', objectFit: 'contain' }}
                    />
                  </div>
                  {selectedCard.name}
                </Modal.Title>
              </Modal.Header>
              <Modal.Body className="p-4">
                <div className="row mb-4">
                  <div className="col-12">
                    <h6 className="fw-bold text-primary mb-2">{t('cardDetails')}</h6>
                    <div className="row">
                      <div className="col-md-6">
                        <p className="mb-1"><strong>{t('annualFee')}:</strong> AED {selectedCard.annualFee || 0}</p>
                        <p className="mb-1"><strong>{t('joiningFee')}:</strong> AED {selectedCard.joiningFee || 0}</p>
                      </div>
                      <div className="col-md-6">
                        <p className="mb-1"><strong>{t('minimumSalary')}:</strong> AED {selectedCard.minimumSalary?.toLocaleString() || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {selectedCard.shortDescription && (
                  <div className="mb-4">
                    <p className="text-muted">{selectedCard.shortDescription}</p>
                  </div>
                )}
                
                {selectedCard.description && (
                  <div className="mb-4">
                    <h6 className="fw-bold text-primary mb-3">{t('description')}</h6>
                    <p style={{ whiteSpace: 'pre-line' }}>{selectedCard.description}</p>
                  </div>
                )}
                
                {selectedCard.featuresAndBenefits?.length > 0 && (
                  <div className="mb-4">
                    <h6 className="fw-bold text-primary mb-3">{t('allBenefits')}</h6>
                    <div className="row">
                      {selectedCard.featuresAndBenefits.map((benefit, index) => (
                        <div key={index} className="col-md-6 mb-2">
                          <div className="d-flex align-items-start">
                            <FontAwesomeIcon icon={faCheck} className="text-success me-2 mt-1 flex-shrink-0" />
                            <span>{benefit.title}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="bg-light p-3 rounded">
                  <h6 className="fw-bold text-primary mb-2">{t('needHelp')}</h6>
                  <p className="mb-2">
                    <FontAwesomeIcon icon={faLocationDot} className="me-2 text-muted" />
                    Office No. 603, Sapphire Tower, Port Saeed, Deira, Dubai
                  </p>
                  <p className="mb-2">
                    <FontAwesomeIcon icon={faPhone} className="me-2 text-muted" />
                    +971 4 435 8112
                  </p>
                  <p className="mb-0">
                    <FontAwesomeIcon icon={faEnvelope} className="me-2 text-muted" />
                    about@fintrekmarketing.com
                  </p>
                </div>
              </Modal.Body>
              <Modal.Footer>
                <button 
                  className="btn btn-primary btn-modern w-100"
                  onClick={() => handleApplyNow(selectedCard)}
                >
                  <FontAwesomeIcon icon={faPhone} className="me-2" />
                  {t('applyNow')}
                </button>
              </Modal.Footer>
            </>
          )}
        </Modal>

        {/* Application Form Modal */}
        <Modal
          show={showApplicationForm}
          onHide={() => setShowApplicationForm(false)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title style={{ color: '#0f2b52' }}>
              {t('applyNow')} - {selectedCard?.name}
            </Modal.Title>
          </Modal.Header>
          <form onSubmit={handleApplicationSubmit}>
            <Modal.Body className="p-4">
              <div className="mb-3">
                <label className="form-label fw-bold">{t('fullName')}</label>
                <InputGroup>
                  <Form.Control
                    type="text"
                    required
                    value={applicationData.fullName}
                    onChange={(e) => setApplicationData({...applicationData, fullName: e.target.value})}
                    placeholder={t('fullName')}
                  />
                </InputGroup>
              </div>
              <div className="mb-3">
                <label className="form-label fw-bold">{t('email')}</label>
                <InputGroup>
                  <Form.Control
                    type="email"
                    required
                    value={applicationData.email}
                    onChange={(e) => setApplicationData({...applicationData, email: e.target.value})}
                    placeholder={t('email')}
                  />
                </InputGroup>
              </div>
              <div className="mb-3">
                <label className="form-label fw-bold">{t('mobileNumber')}</label>
                <InputGroup>
                  <Form.Control
                    type="tel"
                    required
                    value={applicationData.mobile}
                    onChange={(e) => setApplicationData({...applicationData, mobile: e.target.value})}
                    placeholder={t('mobileNumber')}
                  />
                </InputGroup>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <button type="submit" className="btn btn-primary btn-modern w-100">
                <FontAwesomeIcon icon={faCheck} className="me-2" />
                {t('submit')}
              </button>
            </Modal.Footer>
          </form>
        </Modal>
      </div>
    );
  }

  return (
    <div className="card-finder-page">
      <section className="quick-card-finder py-5 pt-5 w-100">
        <div className="container position-relative">
            <div className="row justify-content-center">
              <div className="col-lg-8 text-center mb-5">
                <div style={{ opacity: 1, transform: 'none' }}>
                  <h2 className="display-5 fw-bold mb-3" style={{ color: '#0f2b52' }}>
                    {t('findPerfectProduct')}
                  </h2>
                  <p className="lead mb-4" style={{ color: '#0f2b52', opacity: 0.8 }}>
                    {t('threeQuestions')}
                  </p>
                </div>
              </div>
            </div>          <div className="row justify-content-center">
            <div className="col-lg-6">
              <div className="quick-finder-card bg-white rounded-4 p-5 shadow-lg position-relative">
                <div className="progress mb-4" style={{ height: '4px', backgroundColor: '#e9ecef' }}>
                  <div 
                    className="progress-bar" 
                    style={{ 
                      background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                      width: `${progressPercentage}%`
                    }}
                  ></div>
                </div>

                <div className="text-center mb-4">
                  <span 
                    className="badge bg-light px-3 py-2" 
                    style={{ color: '#0f2b52', fontSize: '1rem' }}
                  >
                    Step {currentStep} of 3
                  </span>
                </div>

                <div className="text-center" style={{ opacity: 1, transform: 'none' }}>
                  <div className="mb-4">
                    <div 
                      className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                      style={{
                        width: '80px',
                        height: '80px',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                      }}
                    >
                      <FontAwesomeIcon 
                        icon={currentQuestion.icon} 
                        className="text-white"
                        style={{ fontSize: '2rem' }}
                      />
                    </div>
                  </div>

                  <h3 className="h4 fw-bold mb-2" style={{ color: '#0f2b52' }}>
                    {currentQuestion.title}
                  </h3>
                  <p className="mb-4" style={{ color: '#0f2b52', opacity: 0.7 }}>
                    {currentQuestion.subtitle}
                  </p>

                  <div className="d-grid gap-3">
                    {currentStep === 1 ? (
                      // Single choice for salary question
                      currentQuestion.options.map((option, index) => (
                        <button
                          key={index}
                          className="btn btn-outline-primary btn-lg text-start py-3 px-4"
                          onClick={() => handleSingleAnswerSelect(option.value, option.label)}
                          style={{
                            border: '2px solid #e9ecef',
                            borderRadius: '12px',
                            transition: '0.3s',
                            color: '#0f2b52',
                            backgroundColor: 'rgba(255, 255, 255, 0.8)'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.borderColor = '#0f2b52';
                            e.target.style.backgroundColor = 'rgba(15, 43, 82, 0.05)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.borderColor = '#e9ecef';
                            e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.8)';
                          }}
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            <span className="fw-medium">{option.label}</span>
                            <FontAwesomeIcon 
                              icon={faArrowRight} 
                              style={{ opacity: 0.6, color: '#0f2b52' }}
                            />
                          </div>
                        </button>
                      ))
                    ) : (
                      // Multiple choice for card type and features questions
                      <>
                        {currentQuestion.options.map((option, index) => {
                          const answerKey = currentStep === 2 ? 'cardType' : 'features';
                          const isSelected = answers[answerKey].includes(option.value);
                          
                          return (
                            <div
                              key={index}
                              className={`btn btn-outline-primary btn-lg text-start py-3 px-4 ${isSelected ? 'active' : ''}`}
                              onClick={() => handleMultipleAnswerSelect(option.value, option.label)}
                              style={{
                                border: `2px solid ${isSelected ? '#0f2b52' : '#e9ecef'}`,
                                borderRadius: '12px',
                                transition: '0.3s',
                                color: '#0f2b52',
                                backgroundColor: isSelected ? 'rgba(15, 43, 82, 0.1)' : 'rgba(255, 255, 255, 0.8)',
                                cursor: 'pointer'
                              }}
                              onMouseEnter={(e) => {
                                if (!isSelected) {
                                  e.target.style.borderColor = '#0f2b52';
                                  e.target.style.backgroundColor = 'rgba(15, 43, 82, 0.05)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!isSelected) {
                                  e.target.style.borderColor = '#e9ecef';
                                  e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.8)';
                                }
                              }}
                            >
                              <div className="d-flex align-items-center justify-content-between">
                                <span className="fw-medium">{option.label}</span>
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => {}} // Handled by parent div onClick
                                    style={{ 
                                      pointerEvents: 'none',
                                      width: '20px',
                                      height: '20px'
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        
                        {/* Next button for multiple choice questions */}
                        <button
                          className="btn btn-primary btn-lg mt-3 py-3 px-4"
                          onClick={handleNextStep}
                          style={{
                            borderRadius: '12px',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            border: 'none'
                          }}
                        >
                          <div className="d-flex align-items-center justify-content-center">
                            <span className="fw-bold me-2">
                              {currentStep === 3 ? t('findMyCard') : t('nextStep')}
                            </span>
                            <FontAwesomeIcon icon={faArrowRight} />
                          </div>
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CardFinder;
